import React, { useCallback, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { useDispatch } from "react-redux";
import Comment from "components/input/Comment.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { RejectDirectlyProps, RejectDirectlyActionData } from "types/modals";
import { rejectDocumentDirectly } from "components/modals/modal.actions";
import { Notification, NotificationType } from "components/common/Notification.component";
import { PATH_TO_DOCUMENT_OVERVIEW } from "components/router/paths";

const RejectDirectly = ({ more, closeModal, loopFocus, initialFocusRef }: RejectDirectlyProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();

    const [comment, setComment] = useState("");
    const [actionRunning, setActionRunning] = useState(false);
    const [displayError, setDisplayError] = useState<Record<string, any> | null>(null);

    const handleSuccess = useCallback(() => {
        const taskIds = more.tasks.map((task) => task.key);
        closeModal();
        more.handledTaskCallback(taskIds, more.navigate);
        more.navigate(PATH_TO_DOCUMENT_OVERVIEW);
    }, [more, closeModal]);

    const handleError = useCallback((error: Record<string, any>) => {
        setDisplayError(error.error ?? error);
        setActionRunning(false);
    }, []);

    const handleReject = useCallback(() => {
        setActionRunning(true);
        setDisplayError(null);

        const taskIds = more.tasks.map((task) => task.key);

        const data: RejectDirectlyActionData = {
            companyId: more.companyId,
            taskIds,
            comment,
        };

        dispatch(rejectDocumentDirectly(data, handleSuccess, handleError));
    }, [comment, more, dispatch, handleSuccess, handleError]);

    const handleCommentChange = useCallback((value: string) => {
        setComment(value);
        setDisplayError(null);
    }, []);

    const okEnabled = !!comment && !actionRunning;

    return (
        <>
            <div className="ga-modal__content">
                {displayError && (
                    <div className="my-16">
                        <Notification
                            type={NotificationType.ERROR}
                            title={displayError.errorTitle}
                            description={displayError.errorMessages}
                        />
                    </div>
                )}
                <Comment
                    ref={initialFocusRef}
                    id="comment"
                    inputHint={translate("popUp.approve.commentHint")}
                    translate={translate}
                    tabIndex={MODAL_CONTENT_TAB_INDEX}
                    value={comment}
                    propagateValue={handleCommentChange}
                    label={translate("popUp.approve.commentLabel")}
                    labelState={`(${translate("popUp.required")})`}
                />
            </div>
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                okEnabled={okEnabled}
                okCallback={handleReject}
                okText={translate("popUp.ok")}
                cancelEnabled={true}
                cancelCallback={closeModal}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        </>
    );
};

export default RejectDirectly;
